import Iconify, { isIconSpec } from '@components/iconify';
import { Stack, SxProps, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import MuiToggleButton from '@mui/material/ToggleButton';
import { useHorizontalScroll } from 'src/hooks/use-horizontal-scroll';
import { JooiceTheme } from 'src/theme/theme';

import { IOption, JToggleGroupVariant } from './models';

/**
 * Props for the JToggleGroup component
 * @interface Props
 */
interface Props {
  /** Currently selected value */
  value: string | number;
  /** Array of options to display as toggle buttons */
  options: IOption[];
  /** Function called when selection changes */
  handleChange: (value: string | number) => void;
  /** Additional Material-UI styling */
  sx?: SxProps;
  /** When true, component takes 100% width of container */
  fullWidth?: boolean;
  /** When true, all options become non-interactive */
  disabled?: boolean;
  /** When true, only displays icons for non-selected items */
  iconsOnly?: boolean;
  /** Variant for custom UI styling */
  jvariant?: JToggleGroupVariant;
}

/**
 * Styled MUI Toggle Button component with custom appearance for Jooice design system
 */
const ToggleButton = styled(MuiToggleButton, {
  shouldForwardProp: (prop) => prop !== 'jvariant',
})<{
  jvariant?: JToggleGroupVariant;
}>(({ theme, jvariant }) => {
  const jooiceTheme = theme as JooiceTheme;
  return {
    color: jooiceTheme.palette.text.secondary,
    backgroundColor: jooiceTheme.palette.pearl[200],
    '&.Mui-selected, &.Mui-selected:hover': {
      color: 'black',
      backgroundColor: 'white',
      border: '2px solid !important',
      borderColor: `${jooiceTheme.palette.pearl[400]} !important`,
    },
    width: 'auto',
    flexShrink: 0,
    padding: jvariant === JToggleGroupVariant.DEFAULT ? '8px' : '4px 8px',
    borderRadius: jvariant === JToggleGroupVariant.DEFAULT ? '4px !important' : '8px 8px 0 0 !important',
    ...(jvariant === JToggleGroupVariant.VARIANT && {
      // margin: '4px 4px 0px 4px !important',
      '&:first-of-type': {
        margin: '4px 4px 4px 0px !important',
      },
      border: '1px solid !important',
      borderColor: `${jooiceTheme.palette.pearl[400]} !important`,
    }),
  };
});

/**
 * A toggle button group component for selecting one option from multiple choices.
 *
 * @param {Props} props - The component props
 * @returns {React.JSX.Element} The rendered toggle group component
 *
 * @example
 * // Basic usage
 * <JToggleGroup
 *   value={selectedView}
 *   options={[
 *     { value: 'list', label: 'List View' },
 *     { value: 'grid', label: 'Grid View' }
 *   ]}
 *   handleChange={setSelectedView}
 * />
 *
 * @example
 * // With icons and custom styling
 * <JToggleGroup
 *   value={selectedTab}
 *   options={[
 *     { value: 'home', label: 'Home', icon: icons.navbar.house },
 *     { value: 'settings', label: 'Settings', icon: icons.navbar.settings },
 *     { value: 'profile', label: 'Profile', icon: icons.commons.user }
 *   ]}
 *   handleChange={setSelectedTab}
 *   fullWidth
 *   iconsOnly
 * />
 */
export default function JToggleGroup({
  value,
  options,
  handleChange,
  sx,
  fullWidth = false,
  disabled = false,
  iconsOnly = false,
  jvariant = JToggleGroupVariant.DEFAULT,
}: Props): React.JSX.Element {
  const theme = useTheme<JooiceTheme>(); // updated to use JooiceTheme type
  const scrollRef = useHorizontalScroll();

  return (
    <ToggleButtonGroup
      fullWidth={fullWidth}
      value={value}
      exclusive
      onChange={(_, newValue) => {
        if (newValue !== null) handleChange(newValue);
      }}
      ref={scrollRef}
      disabled={disabled}
      sx={{
        ...sx,
        ...(jvariant === JToggleGroupVariant.DEFAULT
          ? { backgroundColor: theme.palette.pearl[200], borderRadius: '8px', flexShrink: 0, overflow: 'scroll' }
          : { border: 0, overflow: 'scroll' }),
      }}
    >
      {options.map((o) => {
        return (
          <ToggleButton
            sx={{
              ...(jvariant === JToggleGroupVariant.DEFAULT && { flexGrow: o.value === value ? 1 : 0 }),
            }}
            key={o.value}
            value={o.value}
            disabled={o.disabled}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {o.icon && isIconSpec(o.icon) ? <Iconify icon={o.icon} /> : o.icon}
              {(!iconsOnly || !o.icon) && o.value !== value && (
                <Typography variant="body2" noWrap>
                  {o.label}
                </Typography>
              )}
              {o.value === value && (
                <Typography variant="body2" fontWeight={theme.typography.fontWeightBold} noWrap>
                  {o.label}
                </Typography>
              )}
            </Stack>
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
}
